import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Descriptions, Space, Spin, Tag, Typography, message } from "antd";
import { ArrowRightOutlined, LinkOutlined, ReloadOutlined, SwapOutlined } from "@ant-design/icons";
import { flightOperationFlightService } from "../../../../api/enhancedApi";
import { getFlightTimelineEvents } from "../../../../api/flightOperationApi";
import { FlightTimeDisplay } from "../../../../components/DateTimeDisplay";
import { DERIVATION_TYPE, DERIVATION_TYPE_LABELS, DIVERSION_CATEGORY_BY_CODE, parseDiversionReason } from "../constants";

const { Text } = Typography;

/**
 * 将 additional_info 归一化为对象（后端 JSON 列通常已是对象，此处兼容字符串形态）。
 */
const parseAdditionalInfo = (raw) => {
    if (!raw) return {};
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
            return {};
        }
    }
    return raw;
};

/**
 * 从源占位段的时间线中解析 FLIGHT_DIVERTED 事件，提取两条衍生段 ID。
 *
 * 设计依据：`7.0 飞机备降与衍生航段设计.md` §6.1，FLIGHT_DIVERTED 事件挂载在源占位段 a-b 上，
 * 其 additional_info 携带 diversion_leg_id（a-c）与 recovery_leg_id（c-b）。
 * 时间线 API 是当前唯一暴露 additional_info 的只读端点（事件日志 API 响应模型不含该字段）。
 *
 * @param {number} sourcePlaceholderId 源备降占位段 ID
 * @returns {Promise<{ diversionLegId: number|null, recoveryLegId: number|null }>}
 */
const resolveDiversionLegIds = async (sourcePlaceholderId) => {
    const response = await getFlightTimelineEvents(sourcePlaceholderId, {
        include_business_entity: false,
        page: 1,
        page_size: 100,
    });
    const items = response?.data?.items || [];
    const divertedEvent = items.find((it) => it.event_type === "FLIGHT_DIVERTED");
    if (!divertedEvent) return { diversionLegId: null, recoveryLegId: null };
    const info = parseAdditionalInfo(divertedEvent.additional_info);
    return {
        diversionLegId: info.diversion_leg_id ?? null,
        recoveryLegId: info.recovery_leg_id ?? null,
    };
};

/**
 * 衍生航段展示区块（设计文档 §7.2）
 *
 * 组件主动通过时间线 API 解析备降事件（FLIGHT_DIVERTED）中的衍生段 ID，再按 ID 拉取各航段详情，
 * 从而脱离对列表筛选数据（useMonitor().flights）的依赖——即使用户仅筛选 DIVERTED 状态，
 * COMPLETED 状态的衍生段 a-c / c-b 仍可被完整展示。
 *
 * 数据获取流程：
 *   1. 确定源占位段 ID：占位段视图取自身；衍生段视图取 derived_from_flight_id
 *   2. 拉取源占位段时间线，定位 FLIGHT_DIVERTED 事件的 additional_info，取出 diversion_leg_id 与 recovery_leg_id
 *   3. 按需调用 getFlightOperationById 拉取航段详情（衍生段视图额外拉取源占位段）
 *
 * 根据 selectedFlight 携带的备降元数据与衍生关系字段，渲染两类视图：
 * 1. 当前航班是备降占位段（DIVERTED + diversion_airport）→ 展示其衍生出的 a-c / c-b
 * 2. 当前航班是衍生段（derived_from_flight_id）→ 展示其源占位段与同源兄弟段
 * 3. 无备降字段 → 返回 null
 *
 * @param {Object} props.flight 当前选中航班对象
 * @param {Function} [props.onNavigateFlight] 点击衍生段跳转回调，签名 (flightId) => void
 */
const DiversionLegsSection = ({ flight, onNavigateFlight }) => {
    const [legsMap, setLegsMap] = useState({});
    const [legIds, setLegIds] = useState({ diversionLegId: null, recoveryLegId: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 当前航班是否为备降占位段（含备降元数据）
    const isDiversionPlaceholder = flight?.status === "DIVERTED" && !!flight?.diversion_airport;
    // 当前航班是否为衍生段
    const derivedFromId = flight?.derived_from_flight_id;
    const isDerivedLeg = !!derivedFromId || !!flight?.derivation_type;

    // 源占位段 ID：衍生关系的根，FLIGHT_DIVERTED 事件挂载其上
    const sourcePlaceholderId = isDiversionPlaceholder ? flight?.id : derivedFromId;

    const loadDiversionLegs = useCallback(async () => {
        // 无衍生语义或缺少源占位段锚点时不发起请求
        if (!sourcePlaceholderId) {
            setLegsMap({});
            setLegIds({ diversionLegId: null, recoveryLegId: null });
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            // 1. 从源占位段时间线解析 FLIGHT_DIVERTED 事件，提取衍生段 ID
            const resolvedIds = await resolveDiversionLegIds(sourcePlaceholderId);
            setLegIds(resolvedIds);

            // 2. 汇总需要拉取详情的航段 ID（排除当前航班自身）
            const currentId = flight?.id;
            const targetIds = [
                isDiversionPlaceholder ? null : sourcePlaceholderId, // 衍生段视图需展示源占位段
                resolvedIds.diversionLegId,
                resolvedIds.recoveryLegId,
            ].filter((id) => id != null && id !== currentId);

            // 3. 并行拉取各航段详情，单个失败不阻断其余
            const results = await Promise.all(
                targetIds.map((id) =>
                    flightOperationFlightService
                        .getFlightOperationById(id)
                        .then((res) => ({ id, ok: true, data: res?.data }))
                        .catch((err) => ({ id, ok: false, err })),
                ),
            );

            const map = {};
            let failedCount = 0;
            results.forEach((r) => {
                if (r.ok) {
                    map[r.id] = r.data;
                } else {
                    failedCount += 1;
                    console.error(`拉取衍生段 ID=${r.id} 详情失败:`, r.err);
                }
            });
            setLegsMap(map);

            // 全部失败才视为整体错误（部分失败时展示已成功部分）
            if (failedCount > 0 && Object.keys(map).length === 0) {
                setError("备降航段信息加载失败，请稍后重试");
            }
        } catch (err) {
            console.error("加载衍生航段失败:", err);
            setError("备降航段信息加载失败，请稍后重试");
            setLegsMap({});
        } finally {
            setLoading(false);
        }
    }, [sourcePlaceholderId, isDiversionPlaceholder, flight?.id]);

    useEffect(() => {
        loadDiversionLegs();
    }, [loadDiversionLegs]);

    // 无备降/衍生语义 → 不渲染
    if (!isDiversionPlaceholder && !isDerivedLeg) return null;

    // 解析备降原因
    const parsedReason = parseDiversionReason(flight?.diversion_reason);

    // 渲染航段跳转链接
    const renderFlightLink = (leg) => {
        if (!leg) return null;
        const handleClick = () => {
            if (onNavigateFlight) {
                onNavigateFlight(leg.id);
            } else {
                message.info(`航班 ${leg.flight_number} (ID: ${leg.id}) 详情请从列表点击进入`);
            }
        };
        return (
            <Button type="link" size="small" icon={<LinkOutlined />} onClick={handleClick} style={{ padding: 0 }}>
                {leg.flight_number} ({leg.departure_airport} → {leg.arrival_airport})
            </Button>
        );
    };

    if (loading) {
        return (
            <div style={{ padding: "12px 0", textAlign: "center" }}>
                <Spin size="small" /> <Text type="secondary">加载备降航段信息...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ marginTop: 12 }}>
                <Alert
                    type="error"
                    showIcon
                    message={error}
                    action={
                        <Button size="small" icon={<ReloadOutlined />} onClick={loadDiversionLegs}>
                            重试
                        </Button>
                    }
                />
            </div>
        );
    }

    // 视图 1：备降占位段
    if (isDiversionPlaceholder) {
        const diversionLeg = legIds.diversionLegId ? legsMap[legIds.diversionLegId] : null;
        const recoveryLeg = legIds.recoveryLegId ? legsMap[legIds.recoveryLegId] : null;
        const hasResolvedIds = legIds.diversionLegId || legIds.recoveryLegId;
        const hasRenderableLeg = diversionLeg || recoveryLeg;

        return (
            <div style={{ background: "#faf5ff", border: "1px solid #d3adf7", borderRadius: 6, padding: 12, marginTop: 12 }}>
                <Space style={{ marginBottom: 8 }}>
                    <SwapOutlined style={{ color: "#722ed1" }} />
                    <Text strong>备降航段信息</Text>
                    <Tag color="purple">备降航班</Tag>
                    <Button type="text" size="small" icon={<ReloadOutlined />} onClick={loadDiversionLegs} title="刷新" />
                </Space>
                <Descriptions size="small" column={1} bordered labelStyle={{ width: 110, background: "#fff" }}>
                    <Descriptions.Item label="备降机场">
                        <Tag color="purple">{flight.diversion_airport}</Tag>
                    </Descriptions.Item>
                    {flight.original_arrival_airport && (
                        <Descriptions.Item label="原目的地">
                            <Text>{flight.original_arrival_airport}</Text>
                            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                                (保留原始计划意图)
                            </Text>
                        </Descriptions.Item>
                    )}
                    {flight.diverted_at && (
                        <Descriptions.Item label="备降机场实际落地 (ATA)">
                            <FlightTimeDisplay utcTime={flight.diverted_at} airportCode={flight.diversion_airport} format="YYYY-MM-DD HH:mm" showTimezone={true} />
                        </Descriptions.Item>
                    )}
                    {flight.diversion_reason && (
                        <Descriptions.Item label="备降原因">
                            {parsedReason.code && (
                                <Tag color="geekblue">
                                    {DIVERSION_CATEGORY_BY_CODE[parsedReason.code]?.label || parsedReason.code}
                                </Tag>
                            )}
                            <Text>{parsedReason.description || flight.diversion_reason}</Text>
                        </Descriptions.Item>
                    )}
                </Descriptions>

                <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        备降航段：
                    </Text>
                    {!hasResolvedIds ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            暂未发现备降航段（该航班可能是历史备降航班，或备降记录尚未完成）
                        </Text>
                    ) : !hasRenderableLeg ? (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            备降航段详情加载失败，请点击右上角刷新重试
                        </Text>
                    ) : (
                        <Space direction="vertical" size={6} style={{ width: "100%", marginTop: 6 }}>
                            {diversionLeg && (
                                <div>
                                    <Tag color="green" style={{ marginRight: 6 }}>
                                        ① {DERIVATION_TYPE_LABELS[DERIVATION_TYPE.DIVERSION_LEG]}
                                    </Tag>
                                    {renderFlightLink(diversionLeg)}
                                    {diversionLeg.status && <Tag style={{ fontSize: 11 }}>{diversionLeg.status}</Tag>}
                                </div>
                            )}
                            {recoveryLeg && (
                                <div>
                                    <Tag color="blue" style={{ marginRight: 6 }}>
                                        ② {DERIVATION_TYPE_LABELS[DERIVATION_TYPE.RECOVERY_LEG]}
                                    </Tag>
                                    {renderFlightLink(recoveryLeg)}
                                    {recoveryLeg.status && <Tag style={{ fontSize: 11 }}>{recoveryLeg.status}</Tag>}
                                </div>
                            )}
                        </Space>
                    )}
                </div>
            </div>
        );
    }

    // 视图 2：衍生段（展示其源占位段与同源兄弟段）
    const sourcePlaceholder = sourcePlaceholderId ? legsMap[sourcePlaceholderId] : null;
    const siblingLegs = [legIds.diversionLegId, legIds.recoveryLegId]
        .filter((id) => id && id !== flight?.id)
        .map((id) => legsMap[id])
        .filter(Boolean);

    return (
        <div style={{ background: "#e6f7ff", border: "1px solid #91d5ff", borderRadius: 6, padding: 12, marginTop: 12 }}>
            <Space style={{ marginBottom: 8 }}>
                <ArrowRightOutlined style={{ color: "#108ee9" }} />
                <Text strong>备降航段溯源</Text>
                <Tag color="blue">{DERIVATION_TYPE_LABELS[flight.derivation_type] || flight.derivation_type || "备降航段"}</Tag>
                <Button type="text" size="small" icon={<ReloadOutlined />} onClick={loadDiversionLegs} title="刷新" />
            </Space>
            <Descriptions size="small" column={1} bordered labelStyle={{ width: 110, background: "#fff" }}>
                <Descriptions.Item label="航段类型">
                    <Tag color="blue">{DERIVATION_TYPE_LABELS[flight.derivation_type] || flight.derivation_type}</Tag>
                </Descriptions.Item>
                {sourcePlaceholder ? (
                    <Descriptions.Item label="源备降航班">{renderFlightLink(sourcePlaceholder)}</Descriptions.Item>
                ) : (
                    <Descriptions.Item label="源备降航班">
                        <Text type="secondary">ID: {derivedFromId}（详情加载失败或暂不可用，请点击右上角刷新）</Text>
                    </Descriptions.Item>
                )}
            </Descriptions>
            {siblingLegs.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        关联航段：
                    </Text>
                    <Space wrap style={{ marginTop: 4 }}>
                        {siblingLegs.map((leg) => (
                            <span key={leg.id}>{renderFlightLink(leg)}</span>
                        ))}
                    </Space>
                </div>
            )}
        </div>
    );
};

export default DiversionLegsSection;
